/**
 * The ongoing-jobs board — docs/BUSINESS-RULES.md.
 *
 * Six columns an approved job walks through, from "we need the deposit" to
 * done. Nothing here is a new thing for anyone to keep up to date: every column
 * is decided by the approved checklist your team already ticks, so a job moves
 * because the work moved, not because somebody remembered to drag a card.
 *
 * The columns are deliberately about what is OUTSTANDING rather than what has
 * happened. A board that says "needs final payment" tells you what to do today;
 * one that says "work complete" leaves you to work it out.
 */

import type { JobChecklistItem } from "./types.js";

export type ProductionColumn =
  | "needs_first_payment"
  | "needs_scheduling"
  | "active_work"
  | "needs_final_payment"
  | "clean_up"
  | "closed_won";

export const PRODUCTION_COLUMN_LABEL: Record<ProductionColumn, string> = {
  needs_first_payment: "Needs First Payment",
  needs_scheduling: "Needs Scheduling",
  active_work: "Active Work",
  needs_final_payment: "Needs Final Payment",
  clean_up: "Clean Up",
  closed_won: "Closed Won",
};

export const PRODUCTION_COLUMNS: readonly {
  key: ProductionColumn;
  tone: "red" | "teal" | "blue" | "violet" | "slate" | "green";
}[] = [
  { key: "needs_first_payment", tone: "red" },
  // Teal rather than amber (Jonathon, 2026-08-19: "i'm not a big fan of
  // orange"), and teal rather than slate because Clean Up already has slate
  // and two grey columns on one board is one column nobody can find.
  { key: "needs_scheduling", tone: "teal" },
  { key: "active_work", tone: "blue" },
  { key: "needs_final_payment", tone: "violet" },
  { key: "clean_up", tone: "slate" },
  { key: "closed_won", tone: "green" },
];

/**
 * Jobs under this are not held up for a deposit — they are small enough to do
 * and invoice. Jonathon, 2026-08-03. Integer cents, like all money here.
 */
export const FIRST_PAYMENT_THRESHOLD_CENTS = 100_000;

type ChecklistLike = Pick<JobChecklistItem, "phase" | "label" | "status">;

/** Done, or marked not-applicable — both mean "this is not outstanding". */
const settled = (items: readonly ChecklistLike[], label: string): boolean => {
  const matches = items.filter((i) => i.label === label);
  return (
    matches.length > 0 &&
    matches.every((i) => i.status === "done" || i.status === "na")
  );
};

export interface ProductionInput {
  /** The contract total, in cents. Decides whether a deposit is expected. */
  contractCents: number;
  items: readonly ChecklistLike[];
}

/**
 * Which column an approved job belongs in.
 *
 * Read top down: the first thing still outstanding is the column. A job that
 * has had its final check but still has follow-up steps open is in Clean Up,
 * not Closed Won, however far along the production list it got.
 */
export function productionColumn(job: ProductionInput): ProductionColumn {
  const { items, contractCents } = job;

  if (settled(items, "Closed")) return "closed_won";

  // Money in. A small job is not held up for a deposit, but every job is
  // chased for its final payment — that is the one that pays for the work.
  const needsDeposit = contractCents >= FIRST_PAYMENT_THRESHOLD_CENTS;
  if (needsDeposit && !settled(items, "First Check")) {
    return "needs_first_payment";
  }

  // Scheduling and doing the work. Both steps are maintained from the work
  // orders, so they are right whether this job is a roof, a roof plus other
  // trades, or gutters on their own.
  if (!settled(items, "All Work Scheduled")) return "needs_scheduling";

  if (!settled(items, "All Work Complete")) return "active_work";

  if (!settled(items, "Final Check")) return "needs_final_payment";

  return "clean_up";
}

/** Everything still open in the column's own terms — shown on the card. */
export function outstandingSteps(
  job: ProductionInput,
  column: ProductionColumn,
): string[] {
  const open = (labels: readonly string[]) =>
    labels.filter(
      (l) => job.items.some((i) => i.label === l) && !settled(job.items, l),
    );

  switch (column) {
    case "needs_first_payment":
      return open(["First Check Submitted", "First Check"]);
    case "needs_scheduling":
      return open(["Permit", "Material", "All Work Scheduled"]);
    case "active_work":
      return open(["All Work Complete"]);
    case "needs_final_payment":
      return open(["Final Check Submitted", "Final Check"]);
    case "clean_up":
      return job.items
        .filter(
          (i) =>
            (i.phase === "followup" || i.phase === "financing") &&
            i.status === "pending",
        )
        .map((i) => i.label);
    case "closed_won":
      return [];
  }
}
